// @flow

import type { DecoratedProduct } from '@gandi/cart';

import type { MailboxType } from 'src/modules/mailbox/offers';

export type NormalizedMailbox = {
  /** Display name of the plan (e.g., 'Standard', 'Premium'). */
  name: string,
  /** Storage capacity limit (e.g., '10.0 GB', '50GB'). */
  capacity: string,
  /** Billing duration in months. */
  duration: number,
  /** Number of mailboxes (mostly used for creation workflows). */
  quantity: number,
  /** Technical identifier used to fetch prices in the catalog.
   *
   * @example mailboxv2 - 'standard_new', 'premium_new'
   * @example mailbox-standalone - 'standard', 'premium'
   */
  mailboxType: MailboxType,
  /** Associated email address. */
  email: string,
};

export const normalizeMailboxProduct = (
  product: DecoratedProduct,
): NormalizedMailbox => {
  const payload = product.payload ?? {};

  /* Name Resolution */
  const rawName =
    payload.name ??
    payload.config_name ??
    product.prices?.[0]?.description ??
    '';

  // Capitalize generic price descriptions (e.g., "standard" -> "Standard")
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);

  /* Type Resolution (for catalog matching) */
  const mailboxType =
    payload.mailbox_type ??
    ((payload.config_name ??
      product.prices?.[0]?.description ??
      'unknown') as any as MailboxType);

  /* Capacity Resolution */
  const rawQuota = payload.shopping?.quota_kb;
  const storage = Number.isFinite(rawQuota)
    ? `${(rawQuota / 1024 / 1024).toString()}GB`
    : '';

  let capacity = payload.capacity ?? storage ?? '';
  if (product.productType === 'mailbox-standalone' && !capacity) {
    capacity = mailboxType.toLowerCase().includes('business')
      ? '100GB'
      : mailboxType.toLowerCase().includes('premium')
        ? '50GB'
        : '10GB';
  }

  /* Email Resolution */
  // Standalone renew stores the email address in the product identifier
  const email = payload.email ?? payload.fqdn ?? product.identifier ?? '';

  return {
    name,
    capacity,
    duration: payload.duration ?? payload.duration_month ?? 12,
    quantity: payload.quantity ?? product.quantity ?? 1,
    mailboxType,
    email,
  };
};
